import * as Styled from './styled';
import BranchList from './BranchList';
import WorkspaceList from './WorkspaceList';
import { IRepositoryBranch } from '@/lib/repositoryBranchesApi';
import { IRemoteWorkspace } from '@/lib/workspaces/workspaceApi';
import { ICommit } from '@/types';
import Tabs from '@/ui/Tabs';
import DropdownMenu from '@/ui/DropdownMenu';
import Button from '@/ui/Button';
import { ChevronDownIcon, LayersIcon } from '@radix-ui/react-icons';
import * as Icon from '@/ui/Icon';

type BranchWorkspaceSelectorProps = {
  name?: string;
  label?: string;
  branches: IRepositoryBranch[];
  workspaces?: IRemoteWorkspace[];
  commit?: ICommit | null | undefined;
  selected?: { type: 'branch' | 'workspace' | 'commit'; name: string };
  disabledBranchNames?: string[];
  onChange: (selection: {
    type: 'branch' | 'workspace' | 'commit';
    entity: IRepositoryBranch | IRemoteWorkspace | ICommit;
  }) => void;
};

const getSelectionLabel = (
  selected: { type: 'branch' | 'workspace' | 'commit' },
  selection: IRepositoryBranch | IRemoteWorkspace | ICommit,
) => {
  if (selected.type === 'workspace') {
    const workspace = selection as IRemoteWorkspace;
    return workspace.name || workspace.id.split('-')[0];
  } else if (selected.type === 'branch') {
    const branch = selection as IRepositoryBranch;
    return branch.name;
  } else if (selected.type === 'commit') {
    const commit = selection as ICommit;
    return commit.id.split('-')[0];
  }
};
const BranchWorkspaceSelector: React.FC<BranchWorkspaceSelectorProps> = ({
  branches,
  workspaces,
  commit,
  selected = { type: 'branch', name: 'main' },
  disabledBranchNames = [],
  onChange,
}) => {
  if (!branches || branches.length === 0) {
    return null;
  }
  const defaultTab = selected.type === 'workspace' ? 'workspaces' : 'branches';

  const selectedWorkspace = workspaces?.find(
    (workspace) => workspace.name === selected.name || workspace.id === selected.name,
  );
  const selectedCommit = commit;
  const selectedBranch = branches?.find((branch) => branch.name === selected.name);

  const selectedItem = selectedWorkspace || selectedBranch || selectedCommit;
  const label = getSelectionLabel(selected, selectedItem!);

  const hasWorkspaces = workspaces && workspaces.length > 0;

  return (
    <Styled.BranchWorkspaceSelector>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <Button.Basic small style={{ minWidth: 0, maxWidth: '100%' }}>
            {selected.type === 'workspace' ? (
              <LayersIcon />
            ) : selected.type === 'commit' ? (
              <Icon.Commit size="12px" />
            ) : (
              <Icon.Branch size="12px" />
            )}
            <span className="truncate">{label}</span>
            <ChevronDownIcon />
          </Button.Basic>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          side="bottom"
          align="start"
          style={{ padding: 0, border: hasWorkspaces ? 'none' : undefined }}
        >
          {hasWorkspaces ? (
            <Tabs defaultValue={defaultTab}>
              <Tabs.List>
                <Tabs.Trigger value="branches">Branches</Tabs.Trigger>
                <Tabs.Trigger value="workspaces">Workspaces</Tabs.Trigger>
              </Tabs.List>
              <Tabs.Content value="branches" style={{ padding: 0 }}>
                <BranchList
                  branches={branches}
                  disabledBranchNames={disabledBranchNames}
                  selectedBranch={selectedBranch?.name}
                  commit={commit}
                  onClick={(selection) => onChange(selection)}
                />
              </Tabs.Content>
              <Tabs.Content value="workspaces" style={{ padding: 0 }}>
                <WorkspaceList
                  workspaces={workspaces}
                  selectedWorkspace={selected?.name}
                  onClick={(workspace) => onChange({ type: 'workspace', entity: workspace })}
                />
              </Tabs.Content>
            </Tabs>
          ) : (
            <BranchList
              branches={branches}
              disabledBranchNames={disabledBranchNames}
              selectedBranch={selected.name}
              commit={commit}
              onClick={(selection) => onChange(selection)}
            />
          )}
        </DropdownMenu.Content>
      </DropdownMenu>
    </Styled.BranchWorkspaceSelector>
  );
};

export default BranchWorkspaceSelector;
